import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui";
import type { SettlementFlowKind } from "@/services/adminSettlementService";
import { formatDateTime } from "@/lib/datetime";
import type { SettlementSummaryEmailStats } from "../../settlementUi";
import { SettlementStepCard, type StepCardProps } from "../SettlementStepCard";

export function SummaryEmailStep({
  flow,
  busy,
  noSelection,
  submitting,
  summaryStats,
  onSend,
  disabled = false,
  ...card
}: StepCardProps & {
  flow: SettlementFlowKind;
  busy: boolean;
  noSelection: boolean;
  submitting: boolean;
  summaryStats: SettlementSummaryEmailStats;
  onSend: () => void | Promise<void>;
  disabled?: boolean;
}) {
  const { t } = useTranslation("payments");
  const formDisabled = disabled || busy || noSelection || summaryStats.eligibleCount === 0;
  const isReceiptBased = flow === "RECEIPT_BASED";
  const titleKey = isReceiptBased
    ? "settlements.detail.steps.proformaTitle"
    : "settlements.detail.steps.summaryTitle";
  const descriptionKey = isReceiptBased
    ? "settlements.detail.steps.proformaDesc"
    : "settlements.detail.steps.summaryDesc";
  const buttonKey = isReceiptBased
    ? "settlements.detail.steps.proformaButton"
    : "settlements.detail.steps.summaryButton";
  const resendKey = isReceiptBased
    ? "settlements.detail.steps.proformaResend"
    : "settlements.detail.steps.summaryResend";

  return (
    <SettlementStepCard
      {...card}
      title={t(titleKey)}
      description={t(descriptionKey)}
      disabled={formDisabled}
    >
      <div className="space-y-2">
        <Button
          type="button"
          className="w-full"
          disabled={formDisabled}
          isLoading={submitting}
          onClick={() => void onSend()}
        >
          {summaryStats.sentCount > 0 ? t(resendKey) : t(buttonKey)}
        </Button>
        <p className="text-xs text-surface-500">
          {summaryStats.lastSent
            ? `${t("settlements.detail.emailSentCount", { count: summaryStats.sentCount })} · ${t(
                "settlements.detail.emailLast",
                { when: formatDateTime(summaryStats.lastSent) },
              )}`
            : t("settlements.detail.emailNever")}
        </p>
      </div>
    </SettlementStepCard>
  );
}
